import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Container} from '../../container/container';
import {ContainerService, ContainerUpsertPayload} from '../../container/container.service';
import {Fluid} from 'primeng/fluid';
import {TableLazyLoadEvent, TableModule} from 'primeng/table';
import {StorageService} from '../../services/app/storage.service';
import {AsyncPipe, DatePipe, DecimalPipe, NgIf} from '@angular/common';
import {InputText} from 'primeng/inputtext';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {debounceTime, distinctUntilChanged, fromEvent, Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Select} from 'primeng/select';
import {ManufacturerService} from '../../manufacturer/manufacturer.service';
import {ManufacturerCollection} from '../../manufacturer/manufacturer-collection';
import {Manufacturer} from '../../manufacturer/manufacturer';
import {ButtonDirective, ButtonIcon} from 'primeng/button';
import {Ripple} from 'primeng/ripple';
import {IconComponent} from '../../components/app/layout/icon/icon.component';
import {MultiSelect} from 'primeng/multiselect';

const CONTAINER_MANAGEMENT_LAST_SEARCH           = 'container_management_last_search';
const CONTAINER_MANAGEMENT_LAST_PAGE_SIZE        = 'container_management_last_page_size';
const CONTAINER_MANAGEMENT_LAST_SELECTED_COLUMNS = 'container_management_last_selected_columns';

interface Column {
    field: string;
    header: string;
    editable: boolean;
    type: string|null;
}

@Component({
  selector: 'app-containers',
    imports: [
        Fluid,
        TableModule,
        DatePipe,
        NgIf,
        InputText,
        ReactiveFormsModule,
        FormsModule,
        DecimalPipe,
        AsyncPipe,
        Select,
        ButtonDirective,
        Ripple,
        ButtonIcon,
        IconComponent,
        MultiSelect,
    ],
  templateUrl: './containers.html',
  styleUrl: './containers.scss',
})
export class Containers implements OnInit {

    protected containers!: Container[];
    protected itemsPerPage: number = 10;
    protected loading: boolean = false;
    protected totalNbContainers!: number;
    public search!: string;
    @ViewChild('searchInput', { static: true }) public searchInput!: ElementRef;
    protected manufacturers$!: Observable<ManufacturerCollection>;
    protected selectedManufacturer!: Manufacturer;

    protected cols: Column[] = [];
    protected selectedColumns: Column[] = [];

    constructor(
        protected containerService: ContainerService,
        protected storage: StorageService,
        protected manufacturerService: ManufacturerService
    ) {
    }

    ngOnInit() {
        // keyUp event for search text
        fromEvent(this.searchInput.nativeElement, 'keyup').pipe(
            map((event: any) => {
                return event.target.value;
            }),
            debounceTime(1000),
            distinctUntilChanged()
        ).subscribe((search: string) => {
            // Start search only with at least 4 chars or empty string
            if (search.length >= 4 || search.length === 0) {
                this.loading = true;
                this.search  = search;
                this.updateContainerList(1);
            }
        });

        // Load manufacturers
        this.manufacturers$ = this.manufacturerService.getAll();

        // Initialize columns
        this.cols = [
            { field: 'id', header: 'Container ID', editable: false, type: null },
            { field: 'masterBillOfLadingReference', header: 'Master Bill of Lading', editable: true, type: null },
            { field: 'unLoCodeLoadingPort', header: 'Loading Port', editable: true, type: null },
            { field: 'unLoCodeDestinationPort', header: 'Destination Port', editable: true, type: null },
            { field: 'oceanCarrierCode', header: 'Ocean Carrier', editable: true, type: null },
            { field: 'forecast', header: 'Forecast', editable: false, type: 'forecast' },
            { field: 'lastPushToShippeo', header: 'Last Push To Shippeo', editable: false, type: 'date' }
        ];

        // Read selected columns from local storage
        const selectedColumns: Column[]|null = this.storage.get(CONTAINER_MANAGEMENT_LAST_SELECTED_COLUMNS);
        if (selectedColumns) {
            this.selectedColumns = selectedColumns;
        }
    }

    protected tableUpdated(event: TableLazyLoadEvent): void {
        if (typeof event.rows === 'number' && typeof event.first === 'number') {
            let sortField = null;
            let sortOrder: 'asc'|'desc'|null = null;

            // Determine table sorting
            if (event.sortField !== undefined && typeof event.sortField === 'string') {
                sortField = event.sortField;
                sortOrder = event.sortOrder === 1 ? 'asc' : 'desc';
            }

            // Determine page size
            this.itemsPerPage = event.rows;

            // Update Container list
            this.updateContainerList(event.first / event.rows + 1, sortField, sortOrder);
        } else {
            console.log('Invalid TableLazyLoad event.');
        }
    }

    protected updateContainerList(
        pageNumber: number,
        sortField: string|null = null,
        sortDirection: 'asc'|'desc'|null = null,
        manufacturer: Manufacturer|null = null
    ) {
        this.storage.set(CONTAINER_MANAGEMENT_LAST_PAGE_SIZE, this.itemsPerPage);
        this.search ?
            this.storage.set(CONTAINER_MANAGEMENT_LAST_SEARCH, this.search) :
            this.storage.remove(CONTAINER_MANAGEMENT_LAST_SEARCH);

        this.containerService
            .loadContainers(
                pageNumber,
                this.itemsPerPage,
                sortField,
                sortDirection,
                this.search,
                manufacturer
            )
            .subscribe(containerCollection => {
                this.containers        = containerCollection.getContainers();
                this.totalNbContainers = containerCollection.getTotalItems();
                this.loading           = false;
            });
    }

    protected manufacturerChanged(): void {
        this.updateContainerList(1, null, null, this.selectedManufacturer);
    }

    protected manufacturerCleared(): void {
    }

    protected onRowEditInit(container: Container) {
    }

    protected onRowEditSave(container: Container) {
        const payload: ContainerUpsertPayload & { id: string } = {
            id: container.id,
            item: container.item?.id,
            forecast: container.forecast.iri,
            unLoCodeLoadingPort: container.unLoCodeLoadingPort,
            unLoCodeDestinationPort: container.unLoCodeDestinationPort,
            masterBillOfLadingReference: container.masterBillOfLadingReference,
            oceanCarrierCode: container.oceanCarrierCode,
            moduleWattage: container.moduleWattage,
            etdFactory: container.etdFactory,
            etdPort: container.etdPort,
            etaDestinationPort: container.etaDestinationPort,
            inbondDate: container.inbondDate
        };

        this.containerService.patchContainers([payload]).subscribe(result => {
            console.log(result);
            console.log('container updated');
        });
    }

    protected onRowEditCancel(container: Container, rowIndex: number) {
    }

    protected saveSelectedColumns(): void {
        this.storage.set(CONTAINER_MANAGEMENT_LAST_SELECTED_COLUMNS, this.selectedColumns);
    }
}
